/*
 * Copyright (c) 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */

import {IFilterService} from 'angular';
import RoleUtilService from '../core/service/RoleUtilService';
import {TSEigentumsverhaeltnis} from '../models/enums/TSEigentumsverhaeltnis';
import {TSRechtsform} from '../models/enums/TSRechtsform';
import {TSRole} from '../models/enums/TSRole';
import TSAbstractEntity from '../models/TSAbstractEntity';
import TSGesuchsteller from '../models/TSGesuchsteller';
import TSSchadenfall from '../models/TSSchadenfall';
import TSVersicherungsbeitrag from '../models/TSVersicherungsbeitrag';
import ITranslateService = angular.translate.ITranslateService;

/**
 * Klasse die allgemeine utils Methoden implementiert
 */
export default class DVBernUtil {

    static $inject = ['$filter', 'CONSTANTS', '$translate', 'RoleUtilService'];

    /* @ngInject */
    constructor(private $filter: IFilterService, private CONSTANTS: any, private $translate: ITranslateService, private roleUtilService: RoleUtilService) {
    }

    /**
     * Translates the given string using the angular-translate filter
     * @param toTranslate word to translate
     * @returns {any} translated word
     */
    public translateString(toTranslate: string): string {
        return this.$filter('translate')(toTranslate).toString();
    }

    /**
     * Translates the given list using the angular translate filter
     * @param translationList list of words that will be translated
     * @returns {any} A List of Objects with key and value, where value is the translated word.
     */
    public translateStringList(translationList: Array<any>): Array<any> {
        let listResult: Array<any> = [];
        translationList.forEach((item) => {
            listResult.push({key: item, value: this.translateString(item)});
        });
        return listResult;
    }

    public getJahrtext(rechtsform: TSRechtsform): string {
        if (rechtsform === TSRechtsform.GENOSSENSCHAFT) {
            return this.$translate.instant('JAHR');
        } else {
            return this.$translate.instant('STEUERJAHR');
        }
    }

    public getEinkommenGewinnText(rechtsform: TSRechtsform): string {
        if (rechtsform === TSRechtsform.GENOSSENSCHAFT) {
            return this.$translate.instant('GEWINN');
        } else {
            return this.$translate.instant('STEUERBARES_EINKOMMEN');
        }
    }

    public getVermoegenKapitalText(rechtsform: TSRechtsform): string {
        if (rechtsform === TSRechtsform.GENOSSENSCHAFT) {
            return this.$translate.instant('KAPITAL');
        } else {
            return this.$translate.instant('STEUERBARES_VERMOEGEN');
        }
    }

    public static getIndexOfElementwithID(entityToSearch: TSAbstractEntity, listToSearchIn: Array<any>): number {
        return DVBernUtil.getIndexOfElementwithIDById(entityToSearch.id, listToSearchIn)
    }

    public static getIndexOfElementwithIDById(idToSearch: string | undefined, listToSearchIn: Array<any>): number {
        if (idToSearch) {
            for (let i = 0; i < listToSearchIn.length; i++) {
                if (listToSearchIn[i].id === idToSearch) {
                    return i;
                }
            }
        }
        return -1;
    }

    // set Bankverbindung undefined when IBAN is not available in order to avoid mapping and (storing) Bankverbindung
    // (bean validation would throw an error in case of an invalid / missing IBAN)
    public static setBankverbindungUndefinedIfNoIban(model: TSSchadenfall) {
        if (this.isBankverbindungDefined(model)) {
            if (!this.isIbanDefined(model)) {
                model.gesuchsteller.finanzangaben!.finanzangabenDatenFS.bankverbindung = undefined;
            }
        }
    }

    private static isIbanDefined(model: TSSchadenfall) {
        return model.gesuchsteller.finanzangaben!.finanzangabenDatenFS.bankverbindung!.iban;
    }

    private static isBankverbindungDefined(model: TSSchadenfall) {
        return model.gesuchsteller.finanzangaben && model.gesuchsteller.finanzangaben!.finanzangabenDatenFS.bankverbindung;
    }

    public getRolesAllowedForBankdaten(): Array<TSRole> {
        return this.getRolesAllowedForSteuerdaten().concat(TSRole.ANLAUFSTELLE_SCHAETZER);
    }

    public getRolesAllowedForSteuerdaten(): Array<TSRole> {
        // Betroffenen, die Anlaufstelle und den Fondssuisse. Für der Kanton und Schätzer sind diese Daten nicht sichtbar
        let allowedRoles: Array<TSRole> = [];
        allowedRoles = allowedRoles.concat(this.roleUtilService.getGesuchstellerRoles());
        allowedRoles = allowedRoles.concat(this.roleUtilService.getAnlaufstelleRoles());
        allowedRoles = allowedRoles.concat(this.roleUtilService.getFondssuisseRoles());

        // FS-493 Kantonsexperte (Inspektor) soll finanzielle Verhältnisse und Kontoangaben sehen können
        allowedRoles = allowedRoles.concat(TSRole.KANTON_INSPEKTOR);

        return allowedRoles;
    }

    /**
     * Erzeugt einen random String mit einer Laenge von numberOfCharacters
     * @param numberOfCharacters
     * @returns {string}
     */
    public static generateRandomName(numberOfCharacters: number): string {
        let text = '';
        let possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

        for (let i = 0; i < numberOfCharacters; i++) {
            text += possible.charAt(Math.floor(Math.random() * possible.length));
        }
        return text;
    }

    public static focusFirstInvalidInput() {
        let firstInvalid = angular.element('form .ng-invalid').first();
        if (firstInvalid && firstInvalid.get(0)) {
            let loopcount = 0;
            // sollten wir in einem div sein, suchen wir den ersten subelement
            while ((firstInvalid.get(0).tagName === 'FORM' || firstInvalid.get(0).tagName === 'DIV') && loopcount < 5) {
                firstInvalid = firstInvalid.find('.ng-invalid').first();
                loopcount++;
            }
            if (firstInvalid.get(0).getAttribute('type') === 'hidden') { //Fuer versteckte Elemente geht es mit dem parent weiter da man sie nicht focusen kann
                firstInvalid = firstInvalid.parent();
            }
            if (firstInvalid.get(0).tagName !== 'INPUT') { // Fuer alle Elemente die kein INPUT sind, muessen wir den tabindex setzen, damit focus() funktioniert
                firstInvalid.attr('tabindex', -1).focus();
                console.debug('could not find input element for invalid focus? -> ' + firstInvalid.get(0).tagName);
            } else {
                firstInvalid.focus();
            }
        }
    }

    public getViewTitle(schadenfall: TSSchadenfall): string {
        if (!(schadenfall && schadenfall.isNew())) {
            return this.$translate.instant('SCHADENFALL') + ' - ' + schadenfall.getFallnummerWithZeros();
        } else {
            return this.$translate.instant('NEUER_SCHADENFALL');
        }
    }

    public getVersicherungsbeitragViewTitle(versicherungsbeitrag: TSVersicherungsbeitrag): string {
        if (!(versicherungsbeitrag && versicherungsbeitrag.isNew()) && versicherungsbeitrag.externalSchadenNr !== undefined) {
            return versicherungsbeitrag.externalSchadenNr;
        } else {
            return this.$translate.instant('VERSICHERUNGSBEITRAG');
        }
    }

    public static isNullOrUndefined(data: any): boolean {
        return data === null || data === undefined;
    }

    public static isNotNullOrUndefined(data: any): boolean {
        return !DVBernUtil.isNullOrUndefined(data);
    }


    public isShowTitlePrefix(gesuchsteller: TSGesuchsteller): boolean {
        return gesuchsteller.eigentumsverhaeltnis !== TSEigentumsverhaeltnis.GRUNDEIGENTUEMER_SELBSTBEWIRTSCHAFTER
            && gesuchsteller.eigentumsverhaeltnis !== TSEigentumsverhaeltnis.VERSICHERTER;
    }

}



// WEBPACK FOOTER //
// ./src/app/utils/DVBernUtil.ts